/**
 * Alarm actions with optimistic updates.
 *
 * Every action patches the cached alarm (list pages and the detail query) the
 * moment it is invoked, so the row changes under the pointer immediately; on
 * failure the snapshot is restored and an error toast explains why. Reversible
 * actions (ack, snooze, assign) offer an undo in the confirmation toast.
 */
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { alarmsApi, type SnoozeInput } from '../../lib/api.js';
import { errorMessage } from '../../lib/errors.js';
import type { Alarm, AlarmPage, Severity } from '../../lib/schemas.js';
import { toast } from '../../lib/toast.js';

export const ALARMS_KEY = ['alarms'] as const;

export function alarmDetailKey(id: string): readonly unknown[] {
  return ['alarm', id];
}

export type AlarmActionKind =
  | 'ack'
  | 'unack'
  | 'close'
  | 'reopen'
  | 'snooze'
  | 'unsnooze'
  | 'assign'
  | 'tags'
  | 'severity'
  | 'team';

interface InfiniteAlarmData {
  pages: AlarmPage[];
  pageParams: unknown[];
}

function isInfinite(value: unknown): value is InfiniteAlarmData {
  return (
    typeof value === 'object' && value !== null && Array.isArray((value as InfiniteAlarmData).pages)
  );
}

/** Apply a patch to every cached copy of an alarm; returns an undo function. */
export function patchAlarmInCache(
  client: QueryClient,
  id: string,
  patch: Partial<Alarm>,
): () => void {
  const listSnapshots = client.getQueriesData({ queryKey: ALARMS_KEY });
  const detailSnapshot = client.getQueryData(alarmDetailKey(id));

  client.setQueriesData({ queryKey: ALARMS_KEY }, (data: unknown) => {
    if (!isInfinite(data)) return data;
    return {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        items: page.items.map((alarm) => (alarm.id === id ? { ...alarm, ...patch } : alarm)),
      })),
    };
  });
  client.setQueryData(alarmDetailKey(id), (data: unknown) =>
    data ? { ...(data as Alarm), ...patch } : data,
  );

  return () => {
    for (const [key, value] of listSnapshots) client.setQueryData(key, value);
    if (detailSnapshot !== undefined) client.setQueryData(alarmDetailKey(id), detailSnapshot);
  };
}

export function callAlarmAction(id: string, kind: AlarmActionKind, payload?: unknown) {
  switch (kind) {
    case 'ack':
      return alarmsApi.ack(id, payload as string | undefined);
    case 'unack':
      return alarmsApi.unack(id);
    case 'close':
      return alarmsApi.close(id, payload as string | undefined);
    case 'reopen':
      return alarmsApi.reopen(id);
    case 'snooze':
      return alarmsApi.snooze(id, payload as SnoozeInput);
    case 'unsnooze':
      return alarmsApi.unsnooze(id);
    case 'assign':
      return alarmsApi.assign(id, payload as string | null);
    case 'tags': {
      const { add, remove } = payload as { add: string[]; remove: string[] };
      return alarmsApi.setTags(id, add, remove);
    }
    case 'severity':
      return alarmsApi.setSeverity(id, payload as string);
    case 'team':
      return alarmsApi.setTeam(id, payload as string);
  }
}

export interface UseAlarmActionsResult {
  ack: (alarm: Alarm, note?: string) => void;
  unack: (alarm: Alarm) => void;
  close: (alarm: Alarm, reason?: string) => void;
  reopen: (alarm: Alarm) => void;
  snooze: (alarm: Alarm, input: SnoozeInput) => void;
  assign: (alarm: Alarm, assigneeId: string | null, label?: string) => void;
  setTags: (alarm: Alarm, tags: string[]) => void;
  setSeverity: (alarm: Alarm, severity: Severity) => void;
  setTeam: (alarm: Alarm, teamId: string, label?: string) => void;
  pending: boolean;
}

/**
 * Bound alarm actions. `actorLabel` is only used to paint the optimistic
 * "acknowledged by" chip before the server answer arrives.
 */
export function useAlarmActions(actorLabel?: string): UseAlarmActionsResult {
  const client = useQueryClient();

  interface RunVariables {
    id: string;
    kind: AlarmActionKind;
    payload?: unknown;
    /** Puts the cache back the way it was before the optimistic patch. */
    restore: () => void;
    /** Absent for a silent undo. */
    success?: { message: string; undo?: () => void };
    failure: string;
  }

  // Rollback and toasts live here, at the hook level: callbacks handed to
  // `mutate()` are dropped once the component that called it unmounts, and a
  // row that was patched optimistically must be put back (and its failure
  // said) whether or not the page is still on screen.
  const run = useMutation({
    mutationFn: ({ id, kind, payload }: RunVariables) => callAlarmAction(id, kind, payload),
    onSuccess: (alarm, variables) => {
      if (alarm && typeof alarm === 'object' && 'id' in alarm)
        patchAlarmInCache(client, alarm.id, alarm);
      void client.invalidateQueries({ queryKey: ALARMS_KEY, refetchType: 'none' });
      if (variables.success) {
        const { message, undo } = variables.success;
        toast.success(message, { ...(undo ? { action: { label: 'Undo', run: undo } } : {}) });
      }
    },
    onError: (error, variables) => {
      variables.restore();
      toast.error(variables.failure, { description: errorMessage(error) });
    },
  });

  /** Fire an action that reverses an earlier one; silent unless it fails. */
  const revert = (
    alarm: Alarm,
    kind: AlarmActionKind,
    patch: Partial<Alarm>,
    payload?: unknown,
  ) => {
    const restore = patchAlarmInCache(client, alarm.id, patch);
    run.mutate({ id: alarm.id, kind, payload, restore, failure: 'Could not undo' });
  };

  const perform = (
    alarm: Alarm,
    kind: AlarmActionKind,
    patch: Partial<Alarm>,
    payload: unknown,
    messages: { success: string; failure: string; undo?: () => void },
  ): void => {
    const restore = patchAlarmInCache(client, alarm.id, patch);
    run.mutate({
      id: alarm.id,
      kind,
      payload,
      restore,
      success: { message: messages.success, ...(messages.undo ? { undo: messages.undo } : {}) },
      failure: messages.failure,
    });
  };

  const nowIso = (): string => new Date().toISOString();

  return {
    pending: run.isPending,

    ack: (alarm, note) =>
      perform(
        alarm,
        'ack',
        {
          state: 'acknowledged',
          acknowledged_at: nowIso(),
          acknowledged_by: actorLabel
            ? { id: 'pending', display_name: actorLabel, email: '' }
            : alarm.acknowledged_by,
        },
        note,
        {
          success: 'Alarm acknowledged',
          failure: 'Could not acknowledge the alarm',
          undo: () =>
            revert(alarm, 'unack', {
              state: alarm.state,
              acknowledged_at: alarm.acknowledged_at,
              acknowledged_by: alarm.acknowledged_by,
            }),
        },
      ),

    unack: (alarm) =>
      perform(
        alarm,
        'unack',
        { state: 'open', acknowledged_at: null, acknowledged_by: null },
        undefined,
        { success: 'Acknowledgement removed', failure: 'Could not remove the acknowledgement' },
      ),

    close: (alarm, reason) =>
      perform(
        alarm,
        'close',
        { state: 'closed', closed_at: nowIso(), close_reason: reason ?? null },
        reason,
        { success: 'Alarm closed', failure: 'Could not close the alarm' },
      ),

    reopen: (alarm) =>
      perform(alarm, 'reopen', { state: 'open', closed_at: null }, undefined, {
        success: 'Alarm reopened',
        failure: 'Could not reopen the alarm',
      }),

    snooze: (alarm, input) =>
      perform(
        alarm,
        'snooze',
        {
          state: 'snoozed',
          snoozed_from: alarm.state === 'snoozed' ? alarm.snoozed_from : alarm.state,
          snoozed_until:
            input.until ?? new Date(Date.now() + (input.minutes ?? 60) * 60_000).toISOString(),
        },
        input,
        {
          success: 'Alarm snoozed',
          failure: 'Could not snooze the alarm',
          undo: () =>
            revert(alarm, 'unsnooze', {
              state: alarm.state,
              snoozed_until: alarm.snoozed_until,
              snoozed_from: alarm.snoozed_from,
            }),
        },
      ),

    assign: (alarm, assigneeId, label) =>
      perform(
        alarm,
        'assign',
        {
          assignee: assigneeId
            ? { id: assigneeId, display_name: label ?? assigneeId, email: '' }
            : null,
        },
        assigneeId,
        {
          success: assigneeId ? 'Alarm assigned' : 'Assignee cleared',
          failure: 'Could not change the assignee',
          undo: () =>
            revert(alarm, 'assign', { assignee: alarm.assignee }, alarm.assignee?.id ?? null),
        },
      ),

    // `POST /alarms/:id/tags` takes the difference, not the whole set, so two
    // people editing tags at once do not silently undo each other.
    setTags: (alarm, tags) => {
      const before = new Set(alarm.tags);
      const after = new Set(tags);
      const add = [...after].filter((tag) => !before.has(tag));
      const remove = [...before].filter((tag) => !after.has(tag));
      if (add.length === 0 && remove.length === 0) return;
      perform(
        alarm,
        'tags',
        { tags },
        { add, remove },
        {
          success: 'Tags updated',
          failure: 'Could not update the tags',
        },
      );
    },

    setSeverity: (alarm, severity) =>
      perform(alarm, 'severity', { severity }, severity, {
        success: 'Severity updated',
        failure: 'Could not update the severity',
      }),

    setTeam: (alarm, teamId, label) =>
      perform(
        alarm,
        'team',
        { team_id: teamId, team: { id: teamId, name: label ?? teamId, slug: '' } },
        teamId,
        { success: 'Team updated', failure: 'Could not change the team' },
      ),
  };
}
